import { useState } from 'react';
import { Alert, Field, Input, PageHeader, Select } from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useRequiredProfile } from '../lib/auth';

const RETENTION_OPTIONS = [
  { value: '1y', label: '1 year' },
  { value: '3y', label: '3 years' },
  { value: '5y', label: '5 years' },
  { value: '7y', label: '7 years (ISO 42001 floor, default)' },
  { value: '10y', label: '10 years' },
];

export function AuditSettings() {
  useRequiredProfile();
  const workspaceCtx = useOptionalWorkspace();
  const canManage =
    workspaceCtx?.role === 'owner' || workspaceCtx?.role === 'admin';
  const [retention, setRetention] = useState('7y');
  const [litigationHold, setLitigationHold] = useState(false);
  const [holdMatter, setHoldMatter] = useState('');

  if (!canManage) {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <PageHeader title="Audit settings" subtitle="Owner or admin role required" />
        <Alert variant="warning">You need owner or admin access to configure audit settings.</Alert>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Audit settings"
        subtitle="Retention policy and litigation hold"
      />

      <Alert variant="warning">
        Settings on this page are preview-only and are not persisted yet. Backend storage is scheduled for a future release.
      </Alert>

      <section
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
          display: 'grid',
          gap: 'var(--space-3)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
          Retention posture
        </h2>
        <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
          Audit events older than the retention period are rolled off. ISO 42001 requires a minimum of 7 years for AI governance records.
        </div>
        <Field id="retention" label="Retention period">
          <Select value={retention} onChange={(e) => setRetention(e.target.value)} disabled>
            {RETENTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </Field>
      </section>

      <section
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
          display: 'grid',
          gap: 'var(--space-3)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
          Litigation hold
        </h2>
        <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
          When active, audit event rolloff is suspended for the named matter. Remove the hold when the matter concludes.
        </div>
        <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', fontSize: 'var(--font-size-2xs)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={litigationHold}
            onChange={(e) => setLitigationHold(e.target.checked)}
            disabled
          />
          Litigation hold active
        </label>
        {litigationHold ? (
          <Field id="hold-matter" label="Matter name">
            <Input
              value={holdMatter}
              onChange={(e) => setHoldMatter(e.target.value)}
              placeholder="e.g. Smith v. Acme AI Inc."
            />
          </Field>
        ) : null}
      </section>

    </div>
  );
}
